import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, FileText, Send, Clock, Eye, ShieldCheck, Heart, MapPin, MessageSquare, GraduationCap, Download, LogOut, Mail, Phone, Globe, ChevronDown, ExternalLink } from 'lucide-react';
import AdminMessaging from './AdminMessaging';

const StudentDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [activeChatContext, setActiveChatContext] = useState('admissions');

    useEffect(() => {
        if (id) fetchDetail();
    }, [id]);

    const fetchDetail = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/admin/student/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                setData(result);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (target: 'document' | 'step', targetId: string | number, status: string) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/admin/student/${id}/update-status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ target, targetId: targetId.toString(), status })
            });

            if (response.ok) fetchDetail();
        } catch (error) { console.error("Update failed"); }
    };

    const handleUpdateService = async (service: string, status: string) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/admin/student/${id}/update-service`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ service, status })
            });

            if (response.ok) fetchDetail();
        } catch (error) { console.error("Service update failed"); }
    };

    const handleView = async (docId: number) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/admin/document/view/${docId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch document');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) { console.error(error); alert('Could not preview document'); }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        navigate('/admin/login');
    };

    if (loading) return (
        <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center">
            <div className="text-center">
                <div className="w-8 h-8 border-2 border-[#009B8F] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-400 font-medium">Loading Case File...</p>
            </div>
        </div>
    );
    if (!data) return (
        <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center">
            <p className="text-slate-400">Student not found</p>
        </div>
    );

    const { user, profile, tracking, documents, services, accommodation, shortlist } = data;

    const getDoc = (type: string) => documents?.find((d: any) => d.Type === type);
    const getServiceStatus = (name: string) => services?.find((s: any) => s.Service === name)?.Status || 'locked';

    const getInitials = (name: string) => {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return name.charAt(0).toUpperCase();
    };

    const serviceTypes = [
        { id: 'admissions', label: 'Admissions', icon: GraduationCap },
        { id: 'accommodation', label: 'Accommodation', icon: MapPin },
        { id: 'visa_lab', label: 'Visa Lab', icon: ShieldCheck },
        { id: 'prep_lab', label: 'Prep Lab', icon: FileText },
        { id: 'finance', label: 'Finance', icon: Globe },
        { id: 'career_launchpad', label: 'Career Hub', icon: Heart },
    ];

    const statusColor = (status: string) => {
        switch (status) {
            case 'approved': case 'completed': return 'bg-emerald-500 text-white';
            case 'submitted': case 'active': return 'bg-[#009B8F] text-white';
            case 'locked': return 'bg-slate-100 text-slate-400';
            default: return 'bg-slate-100 text-slate-400';
        }
    };

    const steps = [
        { id: 1, label: 'Create Profile' },
        { id: 2, label: 'Upload Documents' },
        { id: 3, label: 'Documents Received' },
        { id: 4, label: 'Documents Under Review' },
        { id: 5, label: 'Counsellor Assigned' },
        { id: 6, label: 'Counselling Completed' },
        { id: 7, label: 'Applications Submitted' },
        { id: 8, label: 'Conditional Offer Received' },
        { id: 9, label: 'Unconditional Offer / Acceptance' },
        { id: 10, label: 'Proof of Financial Statement' },
        { id: 11, label: 'Final Admission Document' },
        { id: 12, label: 'Ready for Visa Process' }
    ];

    return (
        <div className="min-h-screen bg-[#F1F5F9]">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-6 sticky top-0 z-20">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-4 py-3.5">
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className="p-2 hover:bg-slate-50 rounded-lg transition-colors text-slate-400 hover:text-slate-600"
                        >
                            <ArrowLeft size={18} />
                        </button>
                        <div className="h-5 w-px bg-slate-200" />
                        <img src="/assets/logo.png" alt="NexAI" className="h-7 w-auto object-contain" />
                        <div className="h-5 w-px bg-slate-200" />
                        <div>
                            <h1 className="text-sm font-bold text-[#011E26] flex items-center gap-2">
                                {user.FullName || 'Student'}
                                <span className="text-[10px] font-normal text-slate-400 bg-slate-50 px-2 py-0.5 rounded">
                                    {profile?.StudentID || `NEX-${id}`}
                                </span>
                            </h1>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 hover:border-slate-300 text-sm font-medium transition-all"
                    >
                        <LogOut size={15} /> Logout
                    </button>
                </div>
            </div>

            {/* Service Status Bar */}
            <div className="bg-white border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 overflow-x-auto">
                    {serviceTypes.map(s => {
                        const status = getServiceStatus(s.id);
                        return (
                            <div
                                key={s.id}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${
                                    status === 'approved' || status === 'completed'
                                        ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                                        : status === 'submitted' || status === 'active'
                                            ? 'bg-[#009B8F]/10 text-[#009B8F] border border-[#009B8F]/20'
                                            : 'bg-slate-50 text-slate-400 border border-slate-100'
                                }`}
                            >
                                <s.icon size={11} />
                                {s.label}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto p-6 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">

                {/* Left Column */}
                <div className="lg:col-span-4 space-y-6">

                    {/* Profile Card */}
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center gap-4 mb-5">
                                <div className="w-12 h-12 rounded-full bg-[#009B8F] flex items-center justify-center text-white font-bold text-sm shrink-0">
                                    {getInitials(user.FullName)}
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-bold text-[#011E26] text-sm truncate">{user.FullName || 'Anonymous'}</h3>
                                    <p className="text-xs text-slate-400 truncate">{user.Email}</p>
                                </div>
                            </div>

                            <div className="space-y-2.5">
                                <div className="flex items-center gap-3 text-xs text-slate-600">
                                    <Phone size={13} className="text-slate-400 shrink-0" />
                                    <span>{user.Phone || 'No phone'}</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-slate-600">
                                    <Mail size={13} className="text-slate-400 shrink-0" />
                                    <span className="truncate">{user.Email}</span>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 border-t border-slate-100">
                            <div className="p-4 border-r border-slate-100 text-center">
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Origin</p>
                                <span className="inline-block px-2.5 py-0.5 bg-[#009B8F] text-white rounded-full text-[10px] font-bold">
                                    {user.Country || 'N/A'}
                                </span>
                            </div>
                            <div className="p-4 text-center">
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Target</p>
                                <span className="inline-block px-2.5 py-0.5 bg-[#011E26] text-white rounded-full text-[10px] font-bold">
                                    {profile?.TargetCountry || 'UK'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Service Management */}
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100">
                            <h3 className="text-sm font-bold text-[#011E26] flex items-center gap-2">
                                <ShieldCheck size={16} className="text-[#009B8F]" /> Service Control
                            </h3>
                        </div>
                        <div className="p-4 space-y-2">
                            {serviceTypes.map(s => {
                                const status = getServiceStatus(s.id);
                                return (
                                    <div key={s.id} className="flex items-center justify-between px-4 py-3 bg-slate-50/70 rounded-xl hover:bg-slate-50 transition-colors">
                                        <div className="flex items-center gap-2.5">
                                            <div className={`w-2 h-2 rounded-full ${
                                                status === 'approved' || status === 'completed' ? 'bg-emerald-500' :
                                                status === 'active' || status === 'submitted' ? 'bg-[#009B8F]' : 'bg-slate-300'
                                            }`} />
                                            <span className="text-xs font-semibold text-slate-700">{s.label}</span>
                                        </div>
                                        <select
                                            value={status}
                                            onChange={(e) => handleUpdateService(s.id, e.target.value)}
                                            className="text-[11px] font-semibold bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-[#009B8F]/20 focus:border-[#009B8F]/30 transition-all cursor-pointer appearance-none"
                                        >
                                            <option value="locked">Locked</option>
                                            <option value="active">Active</option>
                                            <option value="submitted">Review</option>
                                            <option value="approved">Approved</option>
                                            <option value="completed">Done</option>
                                        </select>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Documents */}
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100">
                            <h3 className="text-sm font-bold text-[#011E26] flex items-center gap-2">
                                <FileText size={16} className="text-[#009B8F]" /> Documents
                            </h3>
                        </div>
                        <div className="p-4 space-y-1.5">
                            {['passport', 'transcripts', 'degree', 'cv', 'sop', 'lors', 'english', 'other'].map(type => {
                                const doc = getDoc(type);
                                return (
                                    <div key={type} className="group">
                                        <div className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-slate-50 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs shrink-0 ${
                                                    doc
                                                        ? doc.Status === 'approved' ? 'bg-emerald-50 text-emerald-500' : 'bg-[#009B8F]/10 text-[#009B8F]'
                                                        : 'bg-slate-50 text-slate-300'
                                                }`}>
                                                    <FileText size={14} />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-[#011E26] capitalize">{type}</p>
                                                    {doc ? (
                                                        <p className="text-[10px] text-slate-400 mt-0.5">
                                                            {doc.Status === 'approved' ? 'Approved' : doc.Status === 'rejected' ? 'Rejected' : 'Pending review'}
                                                        </p>
                                                    ) : (
                                                        <p className="text-[10px] text-slate-300 mt-0.5">Not uploaded</p>
                                                    )}
                                                </div>
                                            </div>
                                            {doc && (
                                                <button
                                                    onClick={() => handleView(doc.ID)}
                                                    className="p-1.5 text-slate-400 hover:text-[#009B8F] hover:bg-[#009B8F]/5 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                                    title="View Document"
                                                >
                                                    <Download size={14} />
                                                </button>
                                            )}
                                        </div>
                                        {doc && (doc.Status === 'submitted' || doc.Status === 'pending') && (
                                            <div className="flex gap-2 px-4 pb-3">
                                                <button
                                                    onClick={() => handleUpdateStatus('document', doc.ID, 'approved')}
                                                    className="flex-1 bg-emerald-500 text-white text-[10px] py-1.5 rounded-lg font-bold hover:bg-emerald-600 transition-colors"
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => handleUpdateStatus('document', doc.ID, 'rejected')}
                                                    className="flex-1 bg-white text-red-500 border border-red-200 text-[10px] py-1.5 rounded-lg font-bold hover:bg-red-50 transition-colors"
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="lg:col-span-8 space-y-6">

                    {/* Roadmap Milestone Control */}
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-sm font-bold text-[#011E26]">Roadmap Progress</h3>
                            <span className="text-[10px] font-bold text-[#009B8F] bg-[#009B8F]/10 px-2.5 py-1 rounded-full">
                                Step {tracking?.CurrentStep || 1} of {steps.length}
                            </span>
                        </div>
                        <div className="p-5">
                            {/* Progress Bar */}
                            <div className="mb-5">
                                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-[#009B8F] rounded-full transition-all duration-500"
                                        style={{ width: `${((tracking?.CurrentStep || 1) / steps.length) * 100}%` }}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                                {steps.map((step) => {
                                    const isCurrent = tracking?.CurrentStep === step.id;
                                    const isCompleted = (tracking?.CurrentStep || 0) > step.id;

                                    return (
                                        <div
                                            key={step.id}
                                            className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                                                isCurrent
                                                    ? 'bg-[#009B8F]/5 border-2 border-[#009B8F]/20'
                                                    : isCompleted
                                                        ? 'bg-slate-50/50'
                                                        : 'opacity-40'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
                                                    isCompleted
                                                        ? 'bg-[#009B8F] text-white'
                                                        : isCurrent
                                                            ? 'bg-white border-2 border-[#009B8F] text-[#009B8F]'
                                                            : 'bg-white border border-slate-200 text-slate-400'
                                                }`}>
                                                    {isCompleted ? <CheckCircle size={13} /> : step.id}
                                                </div>
                                                <span className={`text-xs font-semibold ${
                                                    isCurrent ? 'text-[#011E26]' : isCompleted ? 'text-slate-500' : 'text-slate-400'
                                                }`}>
                                                    {step.label}
                                                </span>
                                            </div>
                                            {isCurrent && (
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleUpdateStatus('step', user.ID, 'back')}
                                                        className="text-[10px] font-semibold text-slate-500 px-3 py-1.5 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                                                    >
                                                        Back
                                                    </button>
                                                    <button
                                                        onClick={() => handleUpdateStatus('step', user.ID, 'next')}
                                                        className="text-[10px] font-semibold text-white px-4 py-1.5 bg-[#009B8F] rounded-lg hover:bg-[#00857a] transition-colors"
                                                    >
                                                        Advance
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Accommodation Shortlist */}
                    {getServiceStatus('accommodation') !== 'locked' && (
                        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                                <h3 className="text-sm font-bold text-[#011E26]">Accommodation Shortlist</h3>
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#009B8F]/10 text-[#009B8F] rounded-full text-[10px] font-bold">
                                    <Heart size={11} className="fill-[#009B8F]" /> {shortlist?.length || 0} Saved
                                </span>
                            </div>
                            <div className="p-5">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {shortlist?.map((p: any) => (
                                        <div key={p.ID} className="p-4 bg-slate-50/70 rounded-xl border border-slate-100 group hover:border-slate-200 transition-all">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="min-w-0 flex-1">
                                                    <h4 className="text-xs font-bold text-[#011E26] truncate pr-4">{p.Title}</h4>
                                                    <p className="text-[10px] text-slate-400 mt-0.5">{p.Price}</p>
                                                </div>
                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                                    <button className="text-emerald-500 hover:bg-emerald-50 p-1.5 rounded-lg transition-colors"><CheckCircle size={14} /></button>
                                                    <button className="text-red-400 hover:bg-red-50 p-1.5 rounded-lg transition-colors"><XCircle size={14} /></button>
                                                </div>
                                            </div>
                                            {p.SourceLink && (
                                                <a
                                                    href={p.SourceLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-[10px] text-[#009B8F] font-semibold hover:underline"
                                                >
                                                    <ExternalLink size={10} /> View Listing
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                    {(!shortlist || shortlist.length === 0) && (
                                        <div className="col-span-2 text-center py-10 text-slate-300 text-xs">
                                            No properties shortlisted yet.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Support Channels */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                <h3 className="text-sm font-bold text-[#011E26] flex items-center gap-2">
                                    <MessageSquare size={15} className="text-[#009B8F]" /> Admissions
                                </h3>
                                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                            </div>
                            <div className="p-4">
                                <AdminMessaging studentId={id!} context="admissions" />
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                <h3 className="text-sm font-bold text-[#011E26] flex items-center gap-2">
                                    <MapPin size={15} className="text-[#009B8F]" /> Housing
                                </h3>
                                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                            </div>
                            <div className="p-4">
                                <AdminMessaging studentId={id!} context="accommodation" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="max-w-7xl mx-auto px-6 pb-8">
                <div className="text-center text-[10px] text-slate-300 font-bold uppercase tracking-widest py-4">
                    NexAI Global Intelligence
                </div>
            </div>
        </div>
    );
};

export default StudentDetail;
